import { Injectable } from '@angular/core';
import { mapTo, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ApiService } from '../../shared/services/core/api.service';
import { FantasyGameStore } from './fantasy-game.store';
import { FantasyGame, FantasyGameDetailsV2 } from './models/fantasy-game.model';
import moment from 'moment/moment';
import { transaction } from '@datorama/akita/src/lib/transaction';
import { arrayUpdate } from '@datorama/akita/src/lib/arrayUpdate';
import { FantasyGameQuery } from './fantasy-game.query';
import { Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FantasyGameService {
  private gamesCache = new Map<string, Observable<null>>();
  constructor(
    private fantasyGameStore: FantasyGameStore,
    private fantasyGameQuery: FantasyGameQuery,
    private api: ApiService
  ) {}

  fetchFantasyGames() {
    return this.api.get<FantasyGame>('fantasy').pipe(
      tap((games) => {
        games.forEach((game) => {
          game.EndDate = moment.utc(game.EndDate).toDate();
          game.StartDate = moment.utc(game.StartDate).toDate();
          game.GameDate = moment.utc(game.GameDate).toDate();
        });

        this.fantasyGameStore.set(games);
      }),
      mapTo(null)
    );
  }

  @transaction()
  markSelected(gameId: number, playerId: number) {
    this.fantasyGameStore.update(gameId, (entity) => ({
      GamePlayers: arrayUpdate(entity.GamePlayers, () => true, {
        IsSelected: false,
      }),
    }));

    this.fantasyGameStore.update(gameId, (entity) => ({
      GamePlayers: arrayUpdate(entity.GamePlayers, (player) => player.PlayerId === playerId, {
        PlayerId: playerId,
        IsSelected: true,
      }),
    }));

    this.fantasyGameStore.update(gameId, { SelectedPlayerId: playerId });
  }

  confirmSelection(gameId: number) {
    const playerId = this.fantasyGameQuery.getSelectedPlayer(gameId);

    if (!playerId) {
      return of(null);
    }

    return this.api.post<any>(`fantasy/${gameId}/players/${playerId}`, {}).pipe(switchMap(() => this.fetchFantasyGames()));
  }

  fetchFantasyGamesV2(numberOfRecords = 10, outOfDate = false, notStarted = false) {
    const key = `${numberOfRecords}-${outOfDate}-${notStarted}`;

    if (!this.gamesCache.has(key)) {
      const query = `fantasy/v2?numberOfRecords=${numberOfRecords}` + `&outOfDate=${outOfDate}` + `&notStarted=${notStarted}`;

      const request$ = this.api.get<FantasyGame>(query).pipe(
        tap((games) => {
          games.forEach((game) => {
            game.EndDate = moment.utc(game.EndDate).toDate();
            game.StartDate = moment.utc(game.StartDate).toDate();
            game.GameDate = moment.utc(game.GameDate).toDate();
          });

          this.fantasyGameStore.set(games);
        }),
        mapTo(null),
        shareReplay(1)
      );

      this.gamesCache.set(key, request$);
    }

    return this.gamesCache.get(key)!;
  }

  clearCache() {
    this.gamesCache.clear();
  }

  confirmSelectionV2(gameId: number, playerId: number) {
    return this.api.post(`fantasy/${gameId}/players/${playerId}`, {}).pipe(switchMap(() => this.fetchGameDetailsV2(gameId)));
  }

  fetchGameDetailsV2(gameId: number) {
    return this.api.getAnySingle<FantasyGameDetailsV2>(`fantasy/v2/${gameId}`);
  }
}
